/* eslint-disable camelcase */
import { InfoOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  Typography,
  useTheme,
} from '@mui/material'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isAuthenticated } from '../../_helpers/cookie'
import { stampToDate } from '../../_helpers/dateTime'
import { useAppDispatch, useAppSelector } from '../../_helpers/hooks'
import { handleMessages } from '../../_helpers/utilities'
import { IBookingSuccessInformation } from '../../_models/newBookingInterface'
import { StyledToggleButtonGroup } from '../../components/@extended/customToggleGroup'
import { CalenderIcon, ClockIcon } from '../../components/icons'
import { Seat2Icon } from '../../components/icons/seat2Icon'
import {
  bookForRecurringDays,
  bookForRecurringDaysGuest,
  newBookingData,
} from '../../slices/newBookingSlice'
import { errorToast } from '../../slices/toastSlice'

interface IRecurringSuggestionModal {
  onClose: () => void
  bookingSuccessInformation: IBookingSuccessInformation | Record<string, never>
}

const RecurringSuggestionModal = ({
  onClose,
  bookingSuccessInformation,
}: IRecurringSuggestionModal) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [selectedDates, setSelectedDates] = useState<string[]>([])
  const { facilityIdInGuestFlow } = useAppSelector(newBookingData)

  const handleDateToggle = (date: string) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date],
    )
  }

  const dispatch = useAppDispatch()
  const availableDates = bookingSuccessInformation.recurring_available_dates || []
  const handleRecurringBooking = () => {
    if (selectedDates.length === 0) {
      dispatch(errorToast(handleMessages(t('newBooking.newBookingDateRequired'))))
      return
    }
    if (isAuthenticated()) {
      const params = {
        token_id: Number(bookingSuccessInformation.token_id),
        selected_dates: selectedDates,
      }
      dispatch(bookForRecurringDays(params))
    }
    // for guest flow, we have to pass the facility id as well
    else {
      const params = {
        token_id: Number(bookingSuccessInformation.token_id),
        selected_dates: selectedDates,
        facility_id: facilityIdInGuestFlow,
      }
      dispatch(bookForRecurringDaysGuest(params))
    }
    setSelectedDates([]) // Reset selected dates after booking
    // onClose()
  }

  return (
    <>
      <Box
        sx={{
          padding: '1.063rem 1.313rem 1.063rem 1.188rem',
          borderRadius: '0.625rem',
          backgroundColor: 'table.main',
          width: '100%',
        }}
      >
        <Typography variant='subtitle2' color='secondary.dark' my={1}>
          {t('newBooking.bookingSuccessInfoText')}
        </Typography>
        <Box display='flex' flexDirection={{ xs: 'column', sm: 'row' }}>
          <Box flex={4} display={'flex'} alignItems={'center'}>
            <CalenderIcon fill={theme.palette.iconColors.main} width='16' height='21' />
            <Typography fontSize='0.875rem' color='secondary.main' variant='subtitle1' pl={2}>
              {t('newBooking.selectedDateWithColon')}
            </Typography>
          </Box>
          <Box flex={8} pl={{ xs: 4, sm: 0 }}>
            <Typography fontSize='0.875rem' color='common.black' variant='subtitle1'>
              {stampToDate(bookingSuccessInformation.selected_date)}
            </Typography>
          </Box>
        </Box>
        <Box display='flex' mt={2} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Box flex={4} display={'flex'} alignItems={'center'}>
            <ClockIcon fill={theme.palette.iconColors.main} width='20' height='18' />
            <Typography fontSize='0.875rem' color='secondary.main' variant='subtitle1' pl={2}>
              {t('newBooking.selectedSlotWithColon')}
            </Typography>
          </Box>
          <Box flex={8} pl={{ xs: 4, sm: 0 }}>
            <Typography fontSize='0.875rem' color='common.black' variant='subtitle1'>
              {bookingSuccessInformation.selectedSlot}
            </Typography>
          </Box>
        </Box>
        {bookingSuccessInformation?.seats && (
          <Box display='flex' mt={2} flexDirection={{ xs: 'column', sm: 'row' }}>
            <Box flex={4} display={'flex'} alignItems={'center'}>
              <Seat2Icon fill={theme.palette.iconColors.main} width='17' height='19' />
              <Typography fontSize='0.875rem' color='secondary.main' variant='subtitle1' pl={2}>
                {t('newBooking.selectedSeatsWithColon')}
              </Typography>
            </Box>
            <Box flex={8} pl={{ xs: 4, sm: 0 }}>
              <Typography fontSize='0.875rem' color='common.black' variant='subtitle1'>
                {bookingSuccessInformation.seats}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Stack
        mt={3}
        p={2}
        pr={10}
        sx={{
          bgcolor: theme.palette.textGrey.dark,
          borderRadius: '0.625rem',
          display: 'inline-flex',
          alignItems: 'center',
        }}
        direction='row'
        spacing={2}
      >
        <IconButton sx={{ padding: 0 }} component='span'>
          <InfoOutlined color='primary' fontSize='small' />
        </IconButton>
        <Typography variant='subtitle1' color='secondary.main'>
          {bookingSuccessInformation.seats
            ? t('newBooking.seatAvailableOnFutureDates')
            : t('newBooking.slotAvailableOnFutureDates')}
        </Typography>
      </Stack>

      <Grid pt={1}>
        <Grid container columnSpacing={'0.5rem'} p={2}>
          <StyledToggleButtonGroup
            aria-label='available dates'
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              gap: '0.5rem',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            {availableDates?.map((date: string) => {
              const momentDate = moment(date)
              return (
                <ToggleButton
                  value={date}
                  aria-label={date}
                  key={date}
                  sx={{
                    minWidth: '7.688rem',
                    padding: '0.313rem 1.25rem 0.563rem 0.625rem !important',
                    flexShrink: 0,
                  }}
                  onClick={() => handleDateToggle(date)}
                  selected={selectedDates.includes(date)}
                >
                  <Box
                    component={'span'}
                    sx={{
                      fontSize: '1.125rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {momentDate.format('DD')}
                  </Box>{' '}
                  {momentDate.format('dddd')}
                </ToggleButton>
              )
            })}
          </StyledToggleButtonGroup>
        </Grid>
      </Grid>

      <Box mt={3}>
        <Grid container columnSpacing={'1.25rem'} rowSpacing={'1.25rem'}>
          <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
            <Button
              type='submit'
              sx={{ width: '100%' }}
              variant='outlined'
              size='large'
              onClick={onClose}
            >
              {t('newBooking.cancelBtn')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
            <Button
              type='submit'
              sx={{ width: '100%' }}
              variant='contained'
              size='large'
              onClick={() => {
                handleRecurringBooking()
              }}
            >
              {t('newBooking.confirmBtn')}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default RecurringSuggestionModal
